import {
  Autocomplete,
  Box,
  Button,
  Link,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useHistory } from "react-router-dom";
import {
  ClassCard,
  CreateClassDialog,
  EmptyState,
  InlineAlert,
  Loader,
  PagedDataTable,
  SearchField,
  SourceBadge,
} from "src/grading/components";
import type { DataTableColumn } from "src/grading/components/DataTable/DataTable";
import { usePermissions } from "src/grading/hooks/usePermissions";
import PageHeader from "src/grading/layout/PageHeader";
import usePeopleScope from "src/grading/pages/People/usePeopleScope";
import { usePeopleUrlState } from "src/grading/pages/People/usePeopleUrlState";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { SchoolClass } from "src/models";
import { OrganizationView } from "src/models/hierarchy";
import { ClassListItem } from "src/models/staff";
import { useAppDispatch } from "src/store/hooks";
import {
  useGetMyClassesQuery,
  useListClassesQuery,
} from "src/store/reducers/classes/classesApiSlice";
import { useListDistrictsQuery } from "src/store/reducers/districts/districtsApiSlice";
import { clearBreadcrumbs } from "src/store/reducers/navigation/navigationSlice";

const toCard = (schoolClass: SchoolClass, t: TFunction) => (
  <ClassCard
    variant="full"
    id={schoolClass.id}
    name={schoolClass.name}
    gradeLabel={
      schoolClass.gradeLevel
        ? t("classes.gradeLabel", {
            grade: schoolClass.gradeLevel,
            defaultValue: "Grade {{grade}}",
          })
        : t("classes.mixedGrades", { defaultValue: "Mixed grades" })
    }
    year={schoolClass.schoolYear}
    status={schoolClass.source === "manual" ? "manual" : "connected"}
    nextSessionLabel={
      schoolClass.period
        ? t("console.periodLabel", {
            period: schoolClass.period,
            defaultValue: "Period {{period}}",
          })
        : undefined
    }
  />
);

/**
 * Every class in an administrator's scope (FR-005): search, a school filter and paging over
 * `GET /v1/classes`, with "Add class" for viewers who may create classes.
 */
const AdminClassesView = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const url = usePeopleUrlState();
  const scope = usePeopleScope(url.district);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const needsDistrict = scope.isPlatformAdmin && !url.district;

  const { data: districts, isLoading: districtsLoading } =
    useListDistrictsQuery(undefined, { skip: !scope.isPlatformAdmin });
  const { data, isLoading, isFetching, isError, refetch } = useListClassesQuery(
    {
      districtId: scope.requestDistrictId,
      schoolId: url.school,
      search: url.q || undefined,
      limit: rowsPerPage,
      offset: url.page * rowsPerPage,
    },
    { skip: needsDistrict },
  );
  const rows = data?.items ?? [];
  const canCreate = scope.has(permissions.CREATE_CLASSES);
  const createSchools = scope.schools.map((s) => ({ id: s.id, name: s.name }));
  const filtered = Boolean(url.q || url.school);

  const addButton =
    canCreate &&
    !needsDistrict &&
    (scope.ownSchoolId || createSchools.length) ? (
      <Button variant="contained" onClick={() => setCreateOpen(true)}>
        {t("school.addClass")}
      </Button>
    ) : null;

  const districtPicker = scope.isPlatformAdmin ? (
    <Autocomplete<OrganizationView>
      options={districts ?? []}
      value={districts?.find((d) => d.id === url.district) ?? null}
      onChange={(_event, district) => url.setDistrict(district?.id)}
      getOptionLabel={(district) => district.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      loading={districtsLoading}
      loadingText={t("common.loading")}
      noOptionsText={t("picker.noMatches")}
      sx={{ width: { xs: "100%", sm: 300 } }}
      renderInput={(params) => (
        <TextField {...params} size="small" label={t("classes.district")} />
      )}
    />
  ) : null;

  const columns: DataTableColumn<ClassListItem>[] = [
    {
      key: "name",
      header: t("classes.col.name"),
      render: (c) => (
        <Link
          component={RouterLink}
          to={`/grading/classes/${c.id}`}
          sx={{ fontWeight: 800, color: "text.primary" }}
        >
          {c.name}
        </Link>
      ),
    },
    {
      key: "school",
      header: t("classes.col.school"),
      render: (c) => c.schoolName ?? "—",
    },
    {
      key: "grade",
      header: t("classes.col.grade"),
      render: (c) => c.gradeLevel ?? t("classes.mixedGrades"),
    },
    {
      key: "period",
      header: t("classes.col.period"),
      render: (c) => c.period ?? "—",
    },
    {
      key: "students",
      header: t("classes.col.students"),
      align: "right",
      render: (c) => c.studentCount,
    },
    {
      key: "teachers",
      header: t("classes.col.teachers"),
      render: (c) =>
        c.staff.length
          ? c.staff.map((person) => person.name).join(", ")
          : t("classes.noTeachersShort"),
    },
    {
      key: "source",
      header: t("classes.col.source"),
      render: (c) => <SourceBadge source={c.source} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={t("tabs.classes")}
        description={t("classes.adminDescription")}
        actions={
          districtPicker || addButton ? (
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {districtPicker}
              {addButton}
            </Stack>
          ) : undefined
        }
      />
      {needsDistrict ? (
        <EmptyState
          title={t("classes.chooseDistrictTitle")}
          body={t("classes.chooseDistrictBody")}
        />
      ) : (
        <>
          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <SearchField
              label={t("classes.searchLabel")}
              value={url.q}
              onChange={(q) => url.setFilters({ q })}
              sx={{ width: { xs: "100%", sm: 280 } }}
            />
            {scope.schools.length > 0 ? (
              <TextField
                select
                size="small"
                label={t("classes.filterSchool")}
                value={url.school ?? ""}
                onChange={(event) =>
                  url.setFilters({ school: event.target.value || null })
                }
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">{t("classes.allSchools")}</MenuItem>
                {scope.schools.map((school) => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
          </Stack>
          {isError ? (
            <InlineAlert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetch()}>
                  {t("common.retry")}
                </Button>
              }
            >
              {t("classes.loadError")}
            </InlineAlert>
          ) : (
            <PagedDataTable<ClassListItem>
              aria-label={t("classes.tableLabel")}
              columns={columns}
              rows={rows}
              getRowKey={(c) => c.id}
              loading={isLoading || (isFetching && rows.length === 0)}
              total={data?.total ?? 0}
              page={url.page}
              rowsPerPage={rowsPerPage}
              onPageChange={(page) => url.setFilters({ page })}
              onRowsPerPageChange={(next) => {
                setRowsPerPage(next);
                url.setFilters({ page: 0 });
              }}
              emptyState={
                filtered ? (
                  <EmptyState
                    headingLevel={3}
                    title={t("classes.noMatchTitle")}
                    body={t("classes.noMatchBody")}
                    action={
                      <Button variant="outlined" onClick={url.clearFilters}>
                        {t("classes.clearFilters")}
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    headingLevel={3}
                    title={t("classes.emptyTitle")}
                    body={t("school.noClassesBody")}
                    action={addButton}
                  />
                )
              }
            />
          )}
        </>
      )}
      {canCreate ? (
        <CreateClassDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          schoolId={scope.ownSchoolId}
          schools={createSchools}
          onCreated={(created) =>
            history.push(`/grading/classes/${created.id}`)
          }
        />
      ) : null}
    </>
  );
};

/**
 * Classes: administrators (`READ_SCHOOL_STUDENTS` or `UPDATE_CLASSES`) see every class in
 * scope; teachers see the classes they teach as cards that open the class and its roster.
 */
const ClassesPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { hasPermissions } = usePermissions();
  const adminView =
    hasPermissions(
      permissions.READ_SCHOOL_STUDENTS.action,
      permissions.READ_SCHOOL_STUDENTS.subject,
    ) ||
    hasPermissions(
      permissions.UPDATE_CLASSES.action,
      permissions.UPDATE_CLASSES.subject,
    );
  const {
    data: classes,
    isLoading,
    isError,
  } = useGetMyClassesQuery(undefined, { skip: adminView });

  useEffect(() => {
    dispatch(clearBreadcrumbs());
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch]);

  if (adminView) return <AdminClassesView />;
  if (isLoading) return <Loader />;

  return (
    <>
      <PageHeader
        title={t("tabs.classes")}
        description={t("classes.fromRoster", {
          defaultValue:
            "Your classes come from the district roster. You never re-type a name.",
        })}
      />
      {isError && (
        <Box sx={{ mb: 2 }}>
          <InlineAlert severity="error">
            {t("classes.loadError", {
              defaultValue:
                "Could not load your classes. Check your connection and try again.",
            })}
          </InlineAlert>
        </Box>
      )}
      {classes && classes.length === 0 && (
        <EmptyState
          title={t("classes.emptyTitle", {
            defaultValue: "No classes yet",
          })}
          body={t("classes.emptyBody", {
            defaultValue:
              "You are not assigned to any classes yet. Your school administrator assigns classes from the roster.",
          })}
        />
      )}
      {classes && classes.length > 0 && (
        <Box
          component="ul"
          sx={(theme) => ({
            listStyle: "none",
            m: 0,
            p: 0,
            display: "grid",
            gap: 2,
            gridTemplateColumns: "1fr",
            [theme.breakpoints.up(640)]: {
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            },
          })}
        >
          {classes.map((c) => (
            <Box component="li" key={c.id} sx={{ display: "flex" }}>
              {toCard(c, t)}
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

export default ClassesPage;
